/**
 * File: FeedbackPopup.tsx
 * Description: Popup modal for feedback input
 * Author: System
 * Created: 2025-10-06
 * Last Modified: 2025-10-06
 */

import {
  HandThumbUpIcon as HandThumbUpSolidIcon,
  HandThumbDownIcon as HandThumbDownSolidIcon,
} from '@heroicons/react/24/solid';
import React, { useState, useRef, useEffect, useCallback } from 'react';

import { cn } from '../../../shared/utils';
import { logger } from '../../../utils/logger';

import { FeedbackForm } from './FeedbackForm';
import { FeedbackMessage } from './FeedbackMessage';
import { calculatePopupPosition } from './positionUtils';
import type { FeedbackPopupProps } from './types';

export const FeedbackPopup: React.FC<FeedbackPopupProps> = ({
  isVisible,
  onClose,
  onSubmit,
  feedbackType,
  buttonRef,
  isSubmitting = false,
  submitError,
  submitSuccess = false,
}) => {
  const [comment, setComment] = useState('');
  const [includeContext, setIncludeContext] = useState(true);
  const [includeUserName, setIncludeUserName] = useState(true);
  const [position, setPosition] = useState({
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  });
  const [isPositioned, setIsPositioned] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  const calculatePosition = useCallback(() => {
    if (!buttonRef) {
      setPosition({
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      });
      setIsPositioned(true);
      return;
    }
    const newPosition = calculatePopupPosition(buttonRef, popupRef);
    setPosition(newPosition);
    setIsPositioned(true);
  }, [buttonRef]);

  useEffect(() => {
    if (isVisible) {
      // Calculate position immediately when visible
      calculatePosition();

      // Recalculate on scroll or resize
      const handleReposition = () => {
        calculatePosition();
      };

      globalThis.addEventListener('scroll', handleReposition, true);
      globalThis.addEventListener('resize', handleReposition);

      return () => {
        globalThis.removeEventListener('scroll', handleReposition, true);
        globalThis.removeEventListener('resize', handleReposition);
      };
    } else {
      setIsPositioned(false);
    }
  }, [isVisible, calculatePosition]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) {return;}

    try {
      await onSubmit(comment, includeContext, includeUserName);
      // Don't reset form here - let the parent component handle success state
    } catch (error) {
      // Error is handled by parent component
      logger.error('Error submitting feedback:', error);
    }
  };

  const handleCancel = () => {
    setComment('');
    setIncludeContext(true);
    setIncludeUserName(true);
    onClose();
  };

  if (!isVisible) {return null;}

  return (
    <>
      {/* Backdrop */}
      <button
        className="fixed inset-0 z-[9998] bg-transparent cursor-pointer"
        onClick={handleCancel}
        onKeyDown={(e) => e.key === 'Escape' && handleCancel()}
        aria-label="Close feedback popup"
      />

      {/* Popup Content */}
      <div
        ref={popupRef}
        className={`fixed z-[9999] bg-white rounded-lg shadow-xl border border-gray-300 w-80 transform transition-opacity duration-200 ${
          isPositioned ? 'opacity-100' : 'opacity-0'
        }`}
        style={position}
      >
        {/* Header */}
        <div className="p-6 pb-4">
          <div className="flex items-center gap-3 mb-3">
            <div
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center',
                feedbackType === 'up' ? 'bg-success/20' : 'bg-error/20',
              )}
            >
              {feedbackType === 'up' ? (
                <HandThumbUpSolidIcon className="w-4 h-4 text-success" />
              ) : (
                <HandThumbDownSolidIcon className="w-4 h-4 text-error" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-sm text-gray-900">
                {feedbackType === 'up'
                  ? 'Positive Feedback'
                  : 'Constructive Feedback'}
              </h3>
              <p className="text-gray-600 text-xs">Help us improve</p>
            </div>
          </div>

          <FeedbackMessage
            submitSuccess={submitSuccess}
            submitError={submitError || null}
          />

          {/* Form fields - only show if not successfully submitted */}
          {!submitSuccess && (
            <FeedbackForm
              comment={comment}
              setComment={setComment}
              includeContext={includeContext}
              setIncludeContext={setIncludeContext}
              includeUserName={includeUserName}
              setIncludeUserName={setIncludeUserName}
              isSubmitting={isSubmitting}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              feedbackType={feedbackType}
            />
          )}

          {/* Close button for success state */}
          {submitSuccess && (
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={handleCancel}
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
